// OutboundRulesSection.jsx

import React from 'react';

// Utilities
import { cleanString } from '../../apiUtils';
import { getRuleEventLabel } from '../utils/integrationUtils';

// Components
import Button from 'components/button/Button';

/**
 * OutboundRulesSection
 *
 * Renders outbound integration rule rows and row-level actions.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} canManageCredentials - Whether the user can manage integration credentials.
 * @param {boolean} loading - Whether outbound rules are loading.
 * @param {array} sortedRules - Outbound rules sorted for display.
 * @param {Map<string, string>} typeLabelMap - Map of type id to readable label.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {function} onAddRule - Handler to open create rule flow.
 * @param {function} onEditRule - Handler to open edit rule flow.
 * @param {function} onToggleRuleStatus - Handler to toggle rule status.
 * @param {function} onTestRule - Handler to run a test event for a rule.
 * @param {function} onDeleteRule - Handler to open delete confirmation for a rule.
 *
 * @returns {ReactNode} The rendered outbound rules section.
 */
const OutboundRulesSection = ({
    theme,
    canManageCredentials,
    loading,
    sortedRules,
    typeLabelMap,
    busyActionKey,
    onAddRule,
    onEditRule,
    onToggleRuleStatus,
    onTestRule,
    onDeleteRule,
}) => {

    // ----- RENDER ----- //

    return (
        <section
            className="api-integrations-card"
            style={{
                backgroundColor: theme.panel,
                borderColor: theme.backgroundMuted,
            }}
        >
            {/* HEADER */}
            <div
                className="api-integrations-title"
                style={{
                    color: theme.foreground,
                }}
            >
                Outbound Rules
            </div>
            <div
                className="api-integrations-subtitle"
                style={{
                    color: theme.foregroundMuted,
                }}
            >
                Trigger external integrations when workspace data changes.
            </div>
            <div
                className="api-integrations-subtitle"
                style={{
                    color: theme.foregroundMuted,
                }}
            >
                Section A supports three triggers: node created, node updated, and node deleted for a selected type.
            </div>

            {/* ACTIONS */}
            <div className="api-integrations-actions">
                <Button
                    label="+ Add Outbound Rule"
                    iconKey="person_add"
                    onClick={onAddRule}
                    enabled={canManageCredentials && !loading}
                />
            </div>

            {/* LIST */}
            <div className="api-integrations-list">
                {sortedRules.length === 0 && (
                    <div
                        className="api-integrations-empty"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {loading ? 'Loading outbound rules...' : 'No outbound rules yet.'}
                    </div>
                )}

                {sortedRules.map((rule) => {
                    const ruleId = cleanString(rule?.id || rule?.key);
                    const ruleStatus = cleanString(rule?.status || 'active').toLowerCase() === 'paused' ? 'paused' : 'active';
                    const eventLabel = getRuleEventLabel(rule?.event);
                    const typeIdKey = cleanString(rule?.typeId || rule?.typeIdLower).toLowerCase();
                    const typeLabel = typeLabelMap.get(typeIdKey) || 'Unknown type';
                    const endpointUrl = cleanString(rule?.endpoint?.url);
                    const endpointName = cleanString(rule?.endpoint?.name || endpointUrl || 'No destination');

                    return (
                        <div
                            key={ruleId}
                            className="api-integrations-row"
                            style={{
                                borderColor: theme.backgroundMuted,
                            }}
                        >
                            {/* META */}
                            <div className="api-integrations-row-meta">
                                <div
                                    className="api-integrations-row-title"
                                    style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {rule?.name || ''}
                                </div>
                                <div
                                    className="api-integrations-row-subtitle"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    trigger: {eventLabel} for type {typeLabel}
                                </div>
                                <div
                                    className="api-integrations-row-subtitle"
                                    style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    destination: {endpointName}
                                </div>
                            </div>

                            {/* ROW ACTIONS */}
                            <div className="api-integrations-row-actions">
                                <span
                                    className={`api-integrations-status-pill api-integrations-status-pill-${ruleStatus}`}
                                    style={{
                                        borderColor: ruleStatus === 'active' ? (theme.success || '#2ecc71') : theme.foregroundMuted,
                                        color: ruleStatus === 'active' ? (theme.success || '#2ecc71') : theme.foregroundMuted,
                                    }}
                                >
                                    {ruleStatus}
                                </span>

                                <Button
                                    label="Edit"
                                    onClick={() => onEditRule(rule)}
                                    enabled={canManageCredentials && busyActionKey !== `edit:${ruleId}`}
                                    height="30px"
                                    fontSize="12px"
                                    borderRadius="6px"
                                />

                                <Button
                                    label={ruleStatus === 'active' ? 'Pause' : 'Activate'}
                                    onClick={() => onToggleRuleStatus(rule)}
                                    enabled={canManageCredentials && busyActionKey !== `toggle:${ruleId}`}
                                    height="30px"
                                    fontSize="12px"
                                    borderRadius="6px"
                                />

                                <Button
                                    label="Send Test"
                                    onClick={() => onTestRule(rule)}
                                    enabled={canManageCredentials && busyActionKey !== `test:${ruleId}`}
                                    height="30px"
                                    fontSize="12px"
                                    borderRadius="6px"
                                />

                                <Button
                                    label="Delete"
                                    onClick={() => onDeleteRule(rule)}
                                    enabled={canManageCredentials && busyActionKey !== `delete:${ruleId}`}
                                    height="30px"
                                    fontSize="12px"
                                    borderRadius="6px"
                                    foregroundColor={theme.warning}
                                    borderColor={theme.warning}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// Dev Tools
OutboundRulesSection.displayName = 'OutboundRulesSection';

export default OutboundRulesSection;
